/** @file nodeTransform: the Position / Rotation / Scale the Transform section shows at the playhead. */
import type { NodeKey } from '@zilo/pose3d-format/types/clip';
import { nodeKeyOf } from './nodeKeyOf';
import type { JointEdit } from './useJointEdit';

type V3 = [number, number, number];

/**
 * `keyPos` is the key translation (zero without one) and `pos` the node's document `pos` plus it; `rot` is yaw,
 * pitch, roll as the Joint section shows them; `scale` the key scale (1 without one). `*Keyed`: the value comes from
 * a key (or the drag) at this very frame, otherwise it is shown muted.
 */
export type NodeTransform = { keyPos: V3; pos: V3; posKeyed: boolean; rot: V3; rotKeyed: boolean; scale: number; scaleKeyed: boolean };

/** A field of the node's key: the pending drag at this clip and frame first, then the key here or the latest earlier one. */
function keyField<F extends 'pos' | 'scale'>(edit: JointEdit, node: string, field: F): { value: NodeKey[F]; exact: boolean } {
  const o = edit.override;
  const dragged = o !== null && o.clip === edit.clipName && o.frame === edit.frame ? o.nodes[node]?.[field] : undefined;
  if (dragged !== undefined) return { value: dragged, exact: true };
  const view = nodeKeyOf(edit.clip, edit.frame, node, field);
  return { value: view?.key[field], exact: view?.exact === true };
}

/** Without a clip on the playhead (`edit` null): the document pos, the rest rotation, scale 1, nothing keyed. */
export function nodeTransform(nodePos: V3, rest: V3 | undefined, edit: JointEdit | null, node: string): NodeTransform {
  if (edit === null) {
    return { keyPos: [0, 0, 0], pos: [...nodePos], posKeyed: false, rot: rest ?? [0, 0, 0], rotKeyed: false, scale: 1, scaleKeyed: false };
  }
  const p = keyField(edit, node, 'pos');
  const s = keyField(edit, node, 'scale');
  const keyPos: V3 = p.value ?? [0, 0, 0];
  const source = edit.joint.source;
  return {
    keyPos, pos: [nodePos[0] + keyPos[0], nodePos[1] + keyPos[1], nodePos[2] + keyPos[2]], posKeyed: p.exact,
    rot: edit.joint.rot, rotKeyed: source === 'key' || source === 'override', scale: s.value ?? 1, scaleKeyed: s.exact,
  };
}
